import { useEffect } from 'react';

import { allRecommendationCaches } from './recommendations-cache';
import { recommendationsState } from './recommendations-state';

const PURGE_RAN_KEY = 'fooda_rec_purge_ran';
const DEFAULT_TTL_DAYS = 14;

/**
 * Drops rec state + cache entries for every event whose first-write timestamp
 * is past the TTL (default 14 days). Runs at most once per browser session
 * via a sessionStorage marker, so mounting it from a shared root (e.g.
 * AppProvider) is safe even though the marker is checked on every page load.
 *
 * Treats "once per browser session" as a proxy for "on login" — we don't try
 * to detect login events on the frontend; a new browser session is the
 * closest reliable boundary.
 *
 * For each event id purged from `recommendationsState`, every registered rec
 * cache also drops its entries for that event id, so dismissed/added/shown
 * state and cached rec lists stay consistent.
 */
export const usePurgeExpiredRecommendations = (ttlDays = DEFAULT_TTL_DAYS): void => {
  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      if (sessionStorage.getItem(PURGE_RAN_KEY) === 'true') return;
      sessionStorage.setItem(PURGE_RAN_KEY, 'true');

      const purgedIds = recommendationsState.purgeExpiredEvents(ttlDays);
      if (!purgedIds.length) return;

      purgedIds.forEach((eventId) => {
        allRecommendationCaches.forEach((cache) => cache.purgeEvent(eventId));
      });
    } catch {
      // sessionStorage / localStorage unavailable — fail open, will retry
      // next session if storage becomes available again.
    }
  }, [ttlDays]);
};
